import { useQuery } from "@tanstack/react-query"
import { GitCompareArrows, TriangleAlert } from "lucide-react"

import { BOOK_KIND_OF, BookChip } from "@/components/BookMark"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { BOOK_LABELS } from "@/lib/types"
import type { BookId } from "@/lib/types"
import { cn } from "@/lib/utils"

import { fetchCompareBooks } from "@/routes/books/api"
import { useBooksFilters } from "@/routes/books/context"
import { t } from "@/lib/i18n"

/** El backend agrupa por `journal_entry.periodo`, que es AAAA-MM. */
export function periodoDeLaFecha(iso: string): string {
  return (iso || "").slice(0, 7)
}

/**
 * Los tres libros, lado a lado, y de donde sale cada diferencia.
 *
 * ES LA PANTALLA QUE EXPLICA EL PRODUCTO. Un mismo hecho economico se asienta
 * distinto en el libro legal, en el tributario y en el gerencial, y el resultado
 * de los tres no coincide. Eso no es un descuadre: es el punto. Lo que si seria
 * un problema es una diferencia que nadie pueda explicar.
 *
 * Por eso la tabla de abajo no es un anexo: cada fila es una REGLA DE
 * TRATAMIENTO con su efecto en soles. Si los resultados difieren en una cifra
 * que las reglas no suman, hay un ajuste sin rastro, y la pantalla lo dice con
 * esas palabras en vez de dejar que el lector haga la resta y se quede con la
 * duda.
 *
 * El periodo sale del MES de la fecha de corte de la barra y no del rango. El
 * backend agrupa por `journal_entry.periodo`, que es una cadena de mes: pedir
 * "del 12 al 27" no significa nada ahi. Antes que fingir que el rango se
 * respeta, se muestra el mes que de verdad se esta consultando.
 */
export function CompareBooksPage() {
  const { activeTenantId, activeEntityId } = useAuth()
  const { to } = useBooksFilters()
  const periodo = periodoDeLaFecha(to)

  const comparacion = useQuery({
    queryKey: ["compare-books", activeTenantId, activeEntityId, periodo],
    queryFn: () => fetchCompareBooks(periodo),
    enabled: Boolean(activeTenantId && activeEntityId && periodo),
  })

  const porLibro = comparacion.data?.por_libro ?? {}
  const libros = Object.keys(BOOK_LABELS) as BookId[]
  const conDatos = libros.filter((b) => porLibro[b])

  const diferencias = comparacion.data?.diferencias_explicadas ?? []
  const explicado = diferencias.reduce((a, d) => a + Math.abs(Number(d.efecto) || 0), 0)

  const legal = porLibro.STATUTORY_PE?.resultado ?? 0
  const gerencial = porLibro.MANAGEMENT?.resultado ?? 0
  const brecha = gerencial - legal
  // Tolerancia de un sol: los importes se cuantizan a dos decimales por linea y
  // una brecha de céntimos es redondeo, no un ajuste sin rastro.
  const sinExplicar = Math.abs(brecha) > 1 && diferencias.length === 0

  return (
    <div className="space-y-3">
      <MetricRow className="lg:grid-cols-3">
        <MetricCard label={`Resultado legal · ${periodo}`} value={legal}
                    loading={comparacion.isLoading} />
        <MetricCard label={t("Resultado gerencial")} value={gerencial}
                    loading={comparacion.isLoading} />
        <MetricCard
          label={t("Brecha entre ambos")}
          value={brecha}
          loading={comparacion.isLoading}
          tono={sinExplicar ? "alerta" : "neutro"}
        />
      </MetricRow>

      <WorkPanel
        title={t("Resultado por libro")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {t("Periodo")}{" "}{periodo}{" "}{t("— el mes de la fecha de corte, no el rango")}</span>
        }
        footnote={t("Un mismo hecho vive en los tres libros y el resultado de los tres no\n                  coincide. Eso es el producto, no un descuadre: lo que no puede pasar es\n                  que la diferencia no tenga regla.")}
      >
        <DataState
          loading={comparacion.isLoading}
          error={comparacion.error}
          empty={!comparacion.isLoading && conDatos.length === 0}
          emptyMessage={`No hay asientos en ${periodo} para esta sociedad.`}
          emptyHint={t("Cambia la fecha de corte en la barra, o procesa comprobantes del período.")}
          onRetry={() => void comparacion.refetch()}
          rows={3}
          columns={[24, 24, 24, 24]}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Libro")}</TableHead>
                <TableHead className="text-right">{t("Ingresos")}</TableHead>
                <TableHead className="text-right">{t("Gastos")}</TableHead>
                <TableHead className="text-right">{t("Resultado")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conDatos.map((id) => {
                const fila = porLibro[id]
                return (
                  <TableRow key={id}>
                    <TableCell>
                      <BookChip kind={BOOK_KIND_OF[id]} label={BOOK_LABELS[id]} />
                    </TableCell>
                    <TableCell className="num text-right">
                      {formatAmount(Number(fila.ingresos))}
                    </TableCell>
                    <TableCell className="num text-right">
                      {formatAmount(Number(fila.gastos))}
                    </TableCell>
                    <TableCell className="num text-right font-medium">
                      {formatAmount(Number(fila.resultado))}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      {sinExplicar ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/35 bg-destructive-soft p-2.5"
        >
          <TriangleAlert className="mt-px h-4 w-4 shrink-0 text-destructive" />
          <div className="space-y-0.5">
            <p className="text-xs font-semibold text-destructive">
              {t("Los libros difieren en")}{" "}{formatAmount(brecha)}{" "}{t("y ninguna regla lo explica")}</p>
            <p className="max-w-[70ch] text-2xs leading-4 text-foreground/80">
              {t("Toda diferencia entre el resultado legal y el gerencial tiene que salir de una regla de tratamiento identificable. Una que no aparece acá es un ajuste sin rastro: revisa los asientos del período en el diario.")}</p>
          </div>
        </div>
      ) : null}

      <WorkPanel
        title={t("De dónde sale cada diferencia")}
        meta={
          <span className="flex items-center gap-1 text-2xs text-muted-foreground">
            <GitCompareArrows className="h-3 w-3" />
            {diferencias.length}{" "}{t("regla(s) ·")}{" "}{formatAmount(explicado)}{" "}{t("en efecto")}</span>
        }
        footnote={comparacion.data?.nota}
      >
        <DataState
          loading={comparacion.isLoading}
          error={comparacion.error}
          empty={!comparacion.isLoading && diferencias.length === 0}
          emptyMessage={
            Math.abs(brecha) > 1
              ? "Ninguna regla de tratamiento actuó en este período."
              : "Los tres libros dan el mismo resultado en este período."
          }
          emptyHint={
            Math.abs(brecha) > 1
              ? "Y sin embargo los resultados difieren. Es lo que el aviso de arriba señala."
              : "Sin reglas que actúen, el libro gerencial es el legal. Es normal en un período sin gastos con tratamiento propio."
          }
          onRetry={() => void comparacion.refetch()}
          rows={4}
          columns={[28, 16, 12, 16, 28]}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Regla")}</TableHead>
                <TableHead>{t("Libro")}</TableHead>
                <TableHead className="text-right">{t("Asientos")}</TableHead>
                <TableHead className="text-right">{t("Efecto")}</TableHead>
                <TableHead>{t("Por qué")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {diferencias.map((d, i) => (
                <TableRow key={`${d.nombre}-${d.libro}-${i}`}>
                  <TableCell className="font-medium">{d.nombre}</TableCell>
                  <TableCell>
                    {(BOOK_LABELS as Record<string, string>)[d.libro] ?? d.libro}
                  </TableCell>
                  <TableCell className="num text-right">{d.asientos}</TableCell>
                  <TableCell
                    className={cn(
                      "num text-right",
                      Number(d.efecto) < 0 && "text-destructive",
                    )}
                  >
                    {formatAmount(Number(d.efecto))}
                  </TableCell>
                  <TableCell className="max-w-[46ch] text-2xs leading-4 text-muted-foreground">
                    {d.motivo || "La regla no declara motivo."}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>
    </div>
  )
}
